import { db } from '../firebase';
import { collection, getDocs, query, where, getCountFromServer, orderBy, limit, doc, getDoc } from 'firebase/firestore';

/**
 * Obtiene el progreso general de disponibilidad de tickets
 * @returns {Promise<number>} Porcentaje promedio de disponibilidad
 */
export const getProgress = async () => {
  const raffles = await getRafflesWithAvailability();
  if (raffles.length === 0) return 0;

  const totalPercent = raffles.reduce((sum, raffle) => sum + (raffle.percentAvailable || 0), 0);
  return Math.round(totalPercent / raffles.length);
};

/**
 * Obtiene todas las rifas
 * @returns {Promise<Array>} Lista de rifas
 */
export const getRaffles = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'rifas'));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error obteniendo rifas:', error);
    throw new Error('Error al obtener las rifas. Por favor, intente nuevamente.');
  }
};

/**
 * Obtiene las rifas con información de disponibilidad
 * @returns {Promise<Array>} Lista de rifas con porcentaje de disponibilidad
 */
export const getRafflesWithAvailability = async () => {
  try {
    // 1) Solo rifas activas, ordenadas por fecha
    const q = query(
      collection(db, "rifas"),
      where("activa", "==", true),
      orderBy("endDate", "desc")
    );
    // 2) Traer documentos
    const snapshot = await getDocs(q);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Establecer a inicio del día
    //console.log("Fecha actual:", today.toISOString());

    // Crear fecha de mañana para la comparación
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    //console.log("Fecha mañana:", tomorrow.toISOString());

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      const maxTickets = data.maxTickets || 0;
      const ticketsCount = data.ticketsCount || 0;
      const percentAvailable = maxTickets > 0 ? parseFloat(((ticketsCount / maxTickets) * 100).toFixed(2)) : 0;
      
      // Solo si la rifa está activa, verificamos la fecha
      if (data.activa) {
        let rifaDate = null;
        if (data.endDate) {
          // Intentar convertir la fecha según su formato
          try {
            if (typeof data.endDate.toDate === 'function') {
              // Si es un Timestamp de Firestore
              rifaDate = new Date(data.endDate.toDate());
            } else if (data.endDate instanceof Date) {
              // Si ya es un objeto Date
              rifaDate = new Date(data.endDate);
            } else if (typeof data.endDate === 'string') {
              // Si es un string de fecha
              rifaDate = new Date(data.endDate);
            } else if (typeof data.endDate === 'number') {
              // Si es un timestamp numérico
              rifaDate = new Date(data.endDate);
            }
          } catch (error) {
            console.error(`Error al convertir fecha para rifa ${doc.id}:`, error);
          }
        }

        //console.log(`Rifa ${doc.id}:`, {
        //   fechaRifa: rifaDate ? rifaDate.toISOString() : "Sin fecha",
        //   fechaActual: today.toISOString(),
        //   fechaMañana: tomorrow.toISOString(),
        //   esActiva: !rifaDate || rifaDate >= tomorrow,
        //   formatoOriginal: data.endDate ? typeof data.endDate : "Sin fecha"
        // });
        
        // Si no tiene fecha o la fecha es posterior o igual a mañana, se mantiene activa
        const isActive = !rifaDate || rifaDate >= tomorrow;
        
        return { 
          id: doc.id, 
          ...data, 
          ticketsCount, 
          percentAvailable,
          activa: isActive // Actualizar el estado activo basado en la fecha
        };
      }
      
      // Si no está activa, retornamos con activa = false
      return { 
        id: doc.id, 
        ...data, 
        ticketsCount, 
        percentAvailable,
        activa: false
      };
    }).filter(rifa => rifa.activa); // Filtrar solo las rifas que siguen activas
  } catch (error) {
    console.error('Error obteniendo rifas con disponibilidad:', error);
    throw new Error('Error al obtener la disponibilidad de rifas. Por favor, intente nuevamente.');
  }
};

/**
 * Obtiene tickets disponibles para una rifa específica
 * @param {string} rifaId - ID de la rifa
 * @param {number} quantity - Cantidad de tickets a obtener
 * @returns {Promise<Array>} Lista de tickets disponibles
 */
export const getAvailableTickets = async (rifaId, quantity) => {
  try {
    const ticketsQuery = query(
      collection(db, `rifas/${rifaId}/tickets`),
      where('status', '==', 'disponible'),
      orderBy('numeroTicket'),
      limit(quantity)
    );
    const snapshot = await getDocs(ticketsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error obteniendo tickets disponibles:', error);
    throw new Error('Error al obtener tickets disponibles. Por favor, intente nuevamente.');
  }
};

/**
 * Verifica un ticket en todas las rifas
 * @param {string} cedula - Cédula del comprador
 * @returns {Promise<Object>} Resultado de la verificación
 */
export const verifyTicket = async (cedula) => {
  try {
    // Buscamos todas las compras asociadas a la cédula
    const purchasesQuery = query(
      collection(db, 'purchases'),
      where('cedula', '==', cedula)
    );
    
    const purchasesSnapshot = await getDocs(purchasesQuery);
    
    if (purchasesSnapshot.empty) {
      return {
        found: false,
        message: 'No se encontraron tickets asociados a esta cédula.'
      };
    }

    // Procesamos las compras encontradas
    const purchases = purchasesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filtramos solo las compras validadas
    const validPurchases = purchases.filter(purchase => purchase.status === 'pagado');

    if (validPurchases.length === 0) {
      return {
        found: false,
        message: 'No se encontraron tickets validados para esta cédula.'
      };
    }

    // Obtenemos los detalles de las rifas
    const rifaIds = [...new Set(validPurchases.map(purchase => purchase.rifaId))];
    const rifasPromises = rifaIds.map(rifaId => getDoc(doc(db, 'rifas', rifaId)));
    const rifasSnapshots = await Promise.all(rifasPromises);
    
    const rifas = rifasSnapshots.reduce((acc, snap, index) => {
      if (snap.exists()) {
        acc[rifaIds[index]] = snap.data();
      }
      return acc;
    }, {});

    // Organizamos los tickets por rifa
    const ticketsByRifa = validPurchases.reduce((acc, purchase) => {
      if (!acc[purchase.rifaId]) {
        acc[purchase.rifaId] = {
          rifa: rifas[purchase.rifaId],
          tickets: []
        };
      }
      acc[purchase.rifaId].tickets.push(...purchase.tickets);
      return acc;
    }, {});

    return {
      found: true,
      rifas: ticketsByRifa,
      message: `Se encontraron tickets validados en ${Object.keys(ticketsByRifa).length} rifas.`
    };
  } catch (error) {
    console.error('Error verificando ticket:', error);
    throw new Error('Error al verificar el ticket. Por favor, intente nuevamente.');
  }
};

/**
 * Verifica un ticket en una rifa específica
 * @param {string} cedula - Cédula del comprador
 * @param {string} rifaId - ID de la rifa
 * @returns {Promise<Object>} Resultado de la verificación
 */
export const verifyTicketByRifa = async (cedula, rifaId) => {
  try {
    // Primero verificamos que la rifa exista
    const rifaRef = doc(db, 'rifas', rifaId);
    const rifaSnap = await getDoc(rifaRef);
    
    if (!rifaSnap.exists()) {
      throw new Error('La rifa no existe.');
    }

    // Buscamos las compras asociadas a la cédula en esta rifa específica
    const purchasesQuery = query(
      collection(db, 'purchases'),
      where('cedula', '==', cedula),
      where('rifaId', '==', rifaId)
    );
    
    const purchasesSnapshot = await getDocs(purchasesQuery);
    
    if (purchasesSnapshot.empty) {
      return {
        found: false,
        message: 'No se encontraron tickets asociados a esta cédula en esta rifa.'
      };
    }

    // Procesamos las compras encontradas, excluyendo las rechazadas
    const purchases = purchasesSnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(purchase => purchase.status !== 'rechazado');

    if (purchases.length === 0) {
      return {
        found: false,
        message: 'No se encontraron tickets válidos para esta cédula en esta rifa.'
      };
    }

    // Agrupamos las compras por estado
    const groupedPurchases = purchases.map(purchase => ({
      id: purchase.id,
      status: purchase.status,
      tickets: purchase.tickets,
      fecha: purchase.fecha,
      totalAmount: purchase.totalAmount,
      referencia: purchase.referencia
    }));

    return {
      found: true,
      rifa: rifaSnap.data(),
      purchases: groupedPurchases,
      message: `Se encontraron ${groupedPurchases.reduce((acc, purchase) => acc + purchase.tickets.length, 0)} tickets en ${groupedPurchases.length} compras.`
    };
  } catch (error) {
    console.error('Error verificando ticket por rifa:', error);
    throw new Error('Error al verificar el ticket. Por favor, intente nuevamente.');
  }
}; 